/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import Api from "../../../lib/api";
import trackAnalytics from "../../../trackAnalytics";
import { mapArrayToObject } from "../../../utils/arrayUtils";
import { singular } from "../../../utils/stringUtils";
import { setMessage } from "../notification/slice";
import { modulesWithCreator } from "./constants";

const filterEmptyProperties = (entities) => {
    const entitiesClone = _.cloneDeep(entities);
    const propNames = Object.getOwnPropertyNames(entitiesClone);

    for (let i = 0; i < propNames.length; i++) {
        const propName = propNames[i];
        if (
            entitiesClone[propName] === null ||
            entitiesClone[propName] === undefined ||
            !entitiesClone[propName].length
        ) {
            delete entitiesClone[propName];
        }
    }

    return entitiesClone;
};

const initialEntityState = {
    isLoading: false,
    hasErrors: false,
    data: {},
    included: {},
    meta: { totalRecordCount: 1, pageCount: 1 },
};
export const initialState = {
    suppliers: {
        ...initialEntityState,
    },
    drivers: {
        ...initialEntityState,
    },
    trucks: {
        ...initialEntityState,
    },
    truckTypes: {
        ...initialEntityState,
    },
    tripTypes: {
        ...initialEntityState,
    },
    locationCategories: {
        ...initialEntityState,
    },
    organizations: {
        ...initialEntityState,
    },
    users: {
        ...initialEntityState,
    },
    locations: {
        ...initialEntityState,
    },
    trips: {
        ...initialEntityState,
    },
    adminUsers: {
        ...initialEntityState,
    },
    organizationAssignees: {
        ...initialEntityState,
    },
    orders: {
        ...initialEntityState,
    },
    tags: {
        ...initialEntityState,
    },
    devices: {
        ...initialEntityState,
    },
    supplierContracts: {
        ...initialEntityState,
    },
    branches: {
        ...initialEntityState,
    },
    organizationContracts: {
        ...initialEntityState,
    },
    projects: {
        ...initialEntityState,
    },
    contracts: {
        ...initialEntityState,
    },
    supplier_partner_projects: {
        ...initialEntityState
    }

};

const entitySlice = createSlice({
    name: "entities",
    initialState,
    reducers: {
        getEntities: (state, { payload }) => {
            const { entityName } = payload;
            state[entityName].isLoading = true;
        },
        getEntitiesSuccess: (state, { payload }) => {
            const { entityName, entities, meta } = payload;

            state[entityName].data = { ...entities[entityName] };
            state[entityName].meta = meta;

            delete entities[entityName];
            delete entities.meta;
            state[entityName].included = entities;

            state[entityName].hasErrors = false;
            state[entityName].isLoading = false;
        },
        getEntitiesFailure: (state, { payload: { entityName } }) => {
            state[entityName].hasErrors = true;
            state[entityName].isLoading = false;
        },
        editEntity: (state, { payload: { entityName } }) => {
            state[entityName].isLoading = true;
        },
        editEntitySuccess: (state, { payload: { entityName } }) => {
            state[entityName].hasErrors = false;
            state[entityName].isLoading = false;
        },
        editEntityFailure: (state, { payload: { entityName } }) => {
            state[entityName].hasErrors = true;
            state[entityName].isLoading = false;
        },
        deleteEntity: (state, { payload: { entityName } }) => {
            state[entityName].isLoading = true;
        },
        deleteEntitySuccess: (state, { payload: { entityName } }) => {
            state[entityName].hasErrors = false;
            state[entityName].isLoading = false;
        },
        deleteEntityFailure: (state, { payload: { entityName } }) => {
            state[entityName].hasErrors = true;
            state[entityName].isLoading = false;
        },
        newEntity: (state, { payload: { entityName } }) => {
            state[entityName].isLoading = true;
        },
        newEntitySuccess: (state, { payload: { entityName } }) => {
            state[entityName].hasErrors = false;
            state[entityName].isLoading = false;
        },
        newEntityFailure: (state, { payload: { entityName } }) => {
            state[entityName].hasErrors = true;
            state[entityName].isLoading = false;
        },
    },
});

export default entitySlice.reducer;

export const {
    getEntities,
    getEntitiesSuccess,
    getEntitiesFailure,
    editEntity,
    editEntityFailure,
    editEntitySuccess,
    deleteEntity,
    deleteEntitySuccess,
    deleteEntityFailure,
    newEntity,
    newEntitySuccess,
    newEntityFailure,
} = entitySlice.actions;

export const entitySelector = (entityName) => (state) => state.entities[entityName];

export const entityLoadingSelector = (entityName) => (state) =>
    state.entities[entityName].isLoading;
export const entityDataSelector = (entityName) => (state) => state.entities[entityName].data;
export const entityMetaSelector = (entityName) => (state) => state.entities[entityName].meta;

export const includedEntitySelector = (entityName, includedEntityName) => (state) => {
    const included = state.entities[entityName].included;
    const includedEntity = included[includedEntityName];

    return includedEntity;
};

let cancelToken;
const cancelMessage = "Request canceled due to new request.";

export function fetchEntities(
    entityName,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    searchQuery,
    includeEntities,
    includedFields,
    filterQuery,
    groupQuery,
    url
) {
    // Check if there are any previous pending requests
    if (typeof cancelToken != typeof undefined) {
        cancelToken.cancel(cancelMessage);
    }

    // Save the cancel token for the current request
    cancelToken = Api.CancelToken.source();

    const sortParam = sortDirection === "desc" ? "-" + sortField : sortField;
    const filterValues = _.map(filterQuery, (entities, key) => ({
        [`filter[${key}]`]: entities.join(","),
    }));
    const fields = _.map(includedFields, (value, key) => ({
        [`fields[${key}]`]: value,
    }));

    let included = includeEntities;
    if (modulesWithCreator.includes(entityName)) {
        included = included ? included + ",creator" : "creator";
    }

    return async (dispatch) => {
        dispatch(getEntities({ entityName }));

        try {
            const response = await Api.get(url || `/${_.kebabCase(entityName)}`, {
                params: {
                    include: included,
                    ...(pageIndex && { "page[number]": pageIndex + 1 }),
                    ...(pageSize && { "page[size]": pageSize }),
                    sort: groupQuery ? `${groupQuery},${sortParam}` : sortParam,
                    ...(searchQuery && {
                        "filter[q]": searchQuery,
                    }),
                    ...filterEmptyProperties(mapArrayToObject(filterValues)),
                    ...filterEmptyProperties(mapArrayToObject(fields)),
                },
                cancelToken: cancelToken.token,
            });
            dispatch(getEntitiesSuccess({ entityName, ...response }));
        } catch (error) {
            // Ignore showing cancel error
            if (error.message === cancelMessage) {
                return;
            }

            dispatch(getEntitiesFailure({ entityName, error }));
            dispatch(
                setMessage({
                    content: error,
                    title: `Couldn't fetch ${_.lowerCase(entityName)} from the server`,
                    isError: true,
                })
            );
        }
    };
}

export function fetchEntity(entityName, entityId, includeEntities) {
    return async (dispatch) => {
        dispatch(getEntities({ entityName }));

        try {
            const response = await Api.get(`/${_.kebabCase(entityName)}/${entityId}`, {
                params: {
                    ...(includeEntities && { include: includeEntities }),
                },
            });
            dispatch(getEntitiesSuccess({ entityName, ...response }));
        } catch (error) {
            // Ignore showing cancel error
            if (error.message === cancelMessage) {
                return;
            }

            dispatch(getEntitiesFailure({ entityName, error }));
            dispatch(
                setMessage({
                    content: error,
                    title: `Couldn't fetch ${_.lowerCase(entityName)} from the server`,
                    isError: true,
                })
            );
        }
    };
}

export function updateEntity(entityName, id, data) {
    return async (dispatch) => {
        dispatch(editEntity({ entityName }));

        try {
            const filteredData = filterEmptyProperties(data);
            trackAnalytics(`Success - ${entityName} Edited!`);
            const response = await Api.put(`/${_.kebabCase(entityName)}/${id}`, {
                data: {
                    id: id,
                    type: entityName,
                    attributes: {
                        ...filteredData,
                    },
                },
            });
            dispatch(editEntitySuccess({ entityName, ...response }));
            dispatch(
                setMessage({
                    content: `Updated ${_.lowerCase(singular(entityName))}`,
                    isError: false,
                })
            );
        } catch (error) {
            trackAnalytics(`Failure - ${entityName} Editing failed!`);
            dispatch(editEntityFailure({ entityName, error }));
            dispatch(
                setMessage({
                    content: error,
                    title: `Couldn't update ${_.lowerCase(singular(entityName))}`,
                    isError: true,
                })
            );
        }
    };
}

export function destroyEntity(entityName, id) {
    return async (dispatch) => {
        dispatch(deleteEntity({ entityName }));

        try {
            await Api.delete(`/${_.kebabCase(entityName)}/${id}`);
            dispatch(deleteEntitySuccess({ entityName }));
            dispatch(
                setMessage({
                    content: `Deleted ${_.lowerCase(singular(entityName))}`,
                    isError: false,
                })
            );
            trackAnalytics(`Success! - ${entityName} delete success!`);
        } catch (error) {
            dispatch(deleteEntityFailure({ entityName, error }));
            dispatch(
                setMessage({
                    content: error,
                    title: `Couldn't delete ${_.lowerCase(singular(entityName))}`,
                    isError: true,
                })
            );
            trackAnalytics(`Failure! - ${entityName} delete failed!`);
        }
    };
}

export function createEntity(entityName, data, callback) {
    return async (dispatch) => {
        dispatch(newEntity({ entityName }));

        try {
            const response = await Api.post(`/${_.kebabCase(entityName)}`, {
                data: {
                    type: entityName,
                    attributes: {
                        ...data,
                    },
                },
            });

            trackAnalytics(`Success - ${entityName} created!`);

            if (callback) {
                callback(response.entities[entityName]);
            }

            dispatch(newEntitySuccess({ entityName, ...response }));
            dispatch(
                setMessage({
                    content: `Created ${_.lowerCase(singular(entityName))}`,
                    isError: false,
                })
            );
        } catch (error) {
            trackAnalytics(`Failure - ${entityName} failure!`);

            dispatch(newEntityFailure({ entityName, error }));
            dispatch(
                setMessage({
                    content: error,
                    title: `Couldn't create ${_.lowerCase(singular(entityName))}`,
                    isError: true,
                })
            );
        }
    };
}
